import { Link } from 'react-router-dom';
import { Heading } from './ui';
import { TESTIMONIALS, travelerStoriesPublished, type Testimonial } from '@wanderlust/shared';

/**
 * One traveler's words.
 *
 * A real `<blockquote>` with a `<cite>`, because that is what this is: the
 * markup says "someone else said this" without the page having to claim it.
 * The source line is the last and quietest thing in the card, and it is absent
 * entirely for a testimonial sent to Ken directly.
 */
export function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  const { quote, attribution, context, source } = testimonial;

  return (
    <figure className="flex h-full flex-col rounded-xl bg-white p-7 shadow-lg">
      <blockquote className="flex-1 text-lg leading-relaxed text-neutral-700">
        &ldquo;{quote}&rdquo;
      </blockquote>
      <figcaption className="mt-5">
        <cite className="not-italic font-medium text-primary">{attribution}</cite>
        {context && <span className="mt-1 block text-sm text-neutral-600">{context}</span>}
        {source && (
          <span className="mt-2 block text-xs text-neutral-500">
            {source.url ? (
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-neutral-700"
              >
                {source.label}
              </a>
            ) : (
              source.label
            )}
          </span>
        )}
      </figcaption>
    </figure>
  );
}

export interface TravelerStoriesProps {
  /** Defaults to everything published. The stories page passes its own list. */
  testimonials?: Testimonial[];
  /** Set false on the stories page, which is already the place this points to. */
  showAllLink?: boolean;
}

/**
 * "What travelers say", as a homepage band.
 *
 * Renders nothing at all below `MIN_PUBLISHED_TESTIMONIALS`. An empty grid, a
 * skeleton, a single stranded card, or a "testimonials coming soon" line would
 * each be a worse answer than silence: the section is proof, and proof you do
 * not have yet is just a gap with a heading on it.
 *
 * The cards carry whatever attribution the traveler agreed to and, where the
 * quote has a public home, a quiet link to it. Which agency that home belongs
 * to is data (`@wanderlust/shared`'s travelerProof), not markup, so it can
 * change or disappear without this file being touched.
 */

export function TravelerStories({ testimonials = TESTIMONIALS, showAllLink = true }: TravelerStoriesProps) {
  // The same threshold the router and the sitemap use, so the band can never
  // appear while the page it links to is still unpublished.
  if (!travelerStoriesPublished(testimonials)) return null;

  // White, not neutral: the newsletter band above it is neutral-50 and the two
  // would read as one section. This is also how the homepage already floats
  // cards, so the shadow does the separating rather than a rule.
  return (
    <section className="py-28 bg-white" aria-labelledby="traveler-stories-heading">
      <div className="max-w-[1200px] mx-auto px-6">
        <Heading
          level={2}
          id="traveler-stories-heading"
          weight="semibold"
          color="primary"
          className="text-4xl text-center mb-14"
        >
          What travelers say
        </Heading>
        {/* Two columns rather than three: these are sentences, and a third
            column at 1200px cuts the measure short enough to hyphenate. */}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          {testimonials.map((testimonial) => (
            <TestimonialCard key={testimonial.id} testimonial={testimonial} />
          ))}
        </div>
        {showAllLink && (
          <div className="mt-12 text-center">
            <Link
              to="/traveler-stories"
              className="text-sm font-medium uppercase tracking-wide text-primary underline decoration-accent decoration-2 underline-offset-4 hover:text-accent transition-colors"
            >
              Read more traveler stories
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
